import { Link } from 'react-router-dom';
import { useState } from 'react';
import { BarRows, ChartCard, Spinner, Sparkline, TrendChart, money } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { useResource } from '../lib/useResource.js';
import { useLocale, useT } from '../i18n/index.js';

/**
 * Platform metrics (docs/07 "/"). GMV is what passengers paid; platform revenue
 * is our commission of it — they are never conflated, because confusing the two
 * is how a platform talks itself into the wrong business.
 *
 * Only ONE figure on this page is lit: platform revenue. It is the number the
 * business is actually run on, and an accent spent on all four would light none.
 */

interface Overview {
  tenants: Record<string, number>;
  last30Days: { bookings: number; gmvCents: number; commissionCents: number };
  perTenant: Array<{
    tenantId: string;
    name: string;
    slug: string;
    currency: string;
    bookings: number;
    gmvCents: number;
    commissionCents: number;
  }>;
  invoicesAwaitingPayment: number;
  tenantsOnboarding: number;
}

interface TrendMonth {
  month: string;
  bookings: number;
  seats: number;
  grossCents: number;
  refundedCents: number;
  netCents: number;
  commissionCents: number;
}

interface Trends {
  months: number;
  platform: TrendMonth[];
  perTenant: Array<{
    tenantId: string;
    name: string;
    slug: string;
    currency: string;
    months: TrendMonth[];
  }>;
}

function Stat({
  label,
  value,
  hint,
  lit = false,
}: {
  label: string;
  value: string;
  hint: string;
  lit?: boolean;
}) {
  return (
    <div
      className="relative overflow-hidden rounded-xl border p-4"
      style={{
        background: 'var(--ink-850)',
        borderColor: lit ? 'var(--signal-line)' : 'var(--hairline)',
      }}
    >
      {/* The lit card carries a thin amber rule along its top edge — a lamp over
          the bay, not a gradient wash across the card. */}
      {lit && (
        <span
          className="absolute inset-x-0 top-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, var(--signal), transparent)' }}
          aria-hidden
        />
      )}
      <p
        className="text-[0.625rem] font-semibold uppercase tracking-[0.18em]"
        style={{ color: lit ? 'var(--signal)' : 'var(--chalk-faint)' }}
      >
        {label}
      </p>
      <p
        className="font-display mt-2 text-[1.75rem] font-bold leading-none tabular"
        style={{ color: lit ? 'var(--signal)' : 'var(--chalk)' }}
      >
        {value}
      </p>
      <p className="mt-1.5 text-[0.75rem]" style={{ color: 'var(--chalk-faint)' }}>
        {hint}
      </p>
    </div>
  );
}

export function Overview() {
  const t = useT();
  const { locale } = useLocale();
  const [months, setMonths] = useState(12);
  const overview = useResource<Overview>('/platform/overview');
  const trends = useResource<Trends>(`/platform/trends?months=${months}`);

  if (overview.loading) {
    return (
      <Page title={t('overview.title')}>
        <div className="grid place-items-center py-20">
          <Spinner label={t('common.loading')} />
        </div>
      </Page>
    );
  }
  if (!overview.data) {
    return (
      <Page title={t('overview.title')}>
        <p style={{ color: 'var(--halt)' }}>{overview.error}</p>
      </Page>
    );
  }

  const data = overview.data;
  const active = data.tenants.ACTIVE ?? 0;
  const suspended = data.tenants.SUSPENDED ?? 0;

  // Operators bill in their own currencies, so a single total would be meaningless.
  const currencies = [...new Set(data.perTenant.map((row) => row.currency))];
  const single = currencies.length === 1 ? currencies[0]! : null;

  return (
    <Page title={t('overview.title')} description={t('overview.subtitle')}>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label={t('overview.activeTenants')}
          value={String(active)}
          hint={t('overview.activeTenantsHint', {
            onboarding: data.tenantsOnboarding,
            suspended,
          })}
        />
        <Stat
          label={t('overview.bookings')}
          value={data.last30Days.bookings.toLocaleString()}
          hint={t('overview.bookingsHint')}
        />
        <Stat
          label={t('overview.gmv')}
          value={single ? money(data.last30Days.gmvCents, single) : t('overview.mixed')}
          hint={single ? t('overview.gmvHint') : t('overview.mixedHint')}
        />
        <Stat
          lit
          label={t('overview.revenue')}
          value={single ? money(data.last30Days.commissionCents, single) : t('overview.mixed')}
          hint={single ? t('overview.revenueHint') : t('overview.mixedHint')}
        />
      </div>

      {trends.data && (
        <>
          <div className="mt-4 flex justify-end gap-1">
            {[12, 24].map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setMonths(option)}
                className="rounded-md border px-2.5 py-1 text-[0.6875rem] font-semibold uppercase tracking-[0.12em] transition-colors"
                style={{
                  borderColor: months === option ? 'var(--signal-line)' : 'var(--hairline)',
                  color: months === option ? 'var(--signal)' : 'var(--chalk-faint)',
                  background: months === option ? 'var(--ink-800)' : 'transparent',
                }}
                aria-pressed={months === option}
              >
                {option === 12 ? t('overview.window12') : t('overview.window24')}
              </button>
            ))}
          </div>

          <div className="mt-3 grid gap-3 xl:grid-cols-2">
            <ChartCard title={t('overview.trendTitle')} hint={t('overview.trendHint')}>
              <TrendChart
                points={trends.data.platform.map((m) => ({ month: m.month, value: m.bookings }))}
                format={(value) => value.toLocaleString()}
                label={t('overview.trendSeries')}
                locale={locale}
              />
            </ChartCard>
            <ChartCard title={t('overview.seatsTitle')} hint={t('overview.seatsHint')}>
              <TrendChart
                points={trends.data.platform.map((m) => ({ month: m.month, value: m.seats }))}
                format={(value) => value.toLocaleString()}
                label={t('overview.seatsSeries')}
                locale={locale}
                color="var(--chalk-dim)"
              />
            </ChartCard>
          </div>

          <div className="mt-3">
            <ChartCard title={t('overview.perOperator')} hint={t('overview.perOperatorHint')}>
              <BarRows
                emptyLabel={t('overview.noActivity')}
                rows={trends.data.perTenant
                  .map((tenant) => {
                    const commission = tenant.months.reduce((sum, m) => sum + m.commissionCents, 0);
                    const bookings = tenant.months.reduce((sum, m) => sum + m.bookings, 0);
                    return {
                      key: tenant.tenantId,
                      label: tenant.name,
                      // THE BAR ENCODES BOOKINGS, NOT MONEY, and this is the whole
                      // point. Drawing bar lengths from commission would rank
                      // shillings against dollars: an operator billing in TZS gets a
                      // bar a thousand times longer than one billing in USD purely
                      // because of the exchange rate, which says nothing about either
                      // business. Bookings are the same unit everywhere, so they are
                      // the only thing on this page that may be compared by length.
                      // The money still appears — beside the bar, in its own currency.
                      value: bookings,
                      hint: bookings.toLocaleString(),
                      caption: money(commission, tenant.currency),
                    };
                  })
                  .sort((a, b) => b.value - a.value)}
              />
            </ChartCard>
          </div>
        </>
      )}

      <section className="mt-8">
        <h2 className="font-display mb-3 text-[0.9375rem] font-bold">{t('overview.byTenant')}</h2>
        {data.perTenant.length === 0 ? (
          <div
            className="rounded-xl border px-5 py-10 text-center"
            style={{ background: 'var(--ink-850)', borderColor: 'var(--hairline)' }}
          >
            <p className="text-[0.875rem] font-medium">{t('overview.emptyTitle')}</p>
            <p className="mt-1 text-[0.8125rem]" style={{ color: 'var(--chalk-faint)' }}>
              {t('overview.emptyBody')}
            </p>
          </div>
        ) : (
          <div
            className="overflow-x-auto rounded-xl border"
            style={{ background: 'var(--ink-850)', borderColor: 'var(--hairline)' }}
          >
            <table className="w-full border-collapse text-[0.8125rem]">
              <thead>
                <tr>
                  {[
                    t('overview.colTenant'),
                    t('overview.colTrend'),
                    t('overview.colBookings'),
                    t('overview.colGross'),
                    t('overview.colCommission'),
                  ].map((header, index) => (
                    <th
                      key={header}
                      className={`border-b px-4 py-2.5 text-[0.625rem] font-semibold uppercase tracking-[0.16em] ${
                        index === 0 ? 'text-left' : 'text-right'
                      }`}
                      style={{ borderColor: 'var(--hairline)', color: 'var(--chalk-faint)' }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.perTenant.map((row) => (
                  <tr key={row.tenantId} className="transition-colors hover:bg-[var(--ink-800)]">
                    <td className="border-b px-4 py-3" style={{ borderColor: 'var(--hairline)' }}>
                      <Link to={`/tenants/${row.tenantId}`} className="font-medium underline-offset-2 hover:underline">
                        {row.name}
                      </Link>
                      <span className="font-mono ml-2 text-[0.6875rem]" style={{ color: 'var(--chalk-faint)' }}>
                        {row.slug}
                      </span>
                    </td>
                    <td className="border-b px-4 py-3" style={{ borderColor: 'var(--hairline)' }}>
                      <Sparkline
                        values={
                          trends.data?.perTenant.find((s) => s.tenantId === row.tenantId)?.months.map(
                            (m) => m.bookings,
                          ) ?? []
                        }
                        label={`${row.name}: ${t('overview.trendSeries')}`}
                      />
                    </td>
                    <td className="border-b px-4 py-3 text-right tabular" style={{ borderColor: 'var(--hairline)' }}>
                      {row.bookings}
                    </td>
                    <td className="border-b px-4 py-3 text-right tabular" style={{ borderColor: 'var(--hairline)' }}>
                      {money(row.gmvCents, row.currency)}
                    </td>
                    <td
                      className="border-b px-4 py-3 text-right font-medium tabular"
                      style={{ borderColor: 'var(--hairline)', color: 'var(--signal)' }}
                    >
                      {money(row.commissionCents, row.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Page>
  );
}
